/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { LoadingStatus } from '../../primitives/loading-status-indicator/LoadingStatusIndicator';
import { LoadingStepContainer, LoadingStepTitle } from '../loading-step/LoadingStep';

import intlMessages from './messages';

const styles = cssMap({
	titleContainer: {
		display: 'flex',
		flexDirection: 'row',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'space-between',
		borderRadius: token('radius.medium'),
		width: '100%',
		padding: 0,
		backgroundColor: 'transparent',
		textAlign: 'left',
	},
	expandablePressable: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			cursor: 'pointer',
		},
		transition: 'background-color 0.2s ease',
		minHeight: '32px',
	},
	chevronContainer: {
		marginRight: token('space.100'),
	},
	expandedContent: {
		width: '100%',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		wordBreak: 'break-word',
	},
});

export type ExpandableLoadingStepProps = {
	testId?: string;
	/** main text content of the loading step  */
	title?: string;
	/** determines the status icon displayed */
	status: LoadingStatus;
	/** called when the step is clicked for expansion */
	onClick: () => void;
	/** expanded state */
	isExpanded?: boolean;
	isLastItem?: boolean;
	/** content displayed when item is expanded */
	children: ReactNode;
	/** Called only when the user clicks to expand/collapse — not on programmatic state changes. */
	onExpand?: (expanded: boolean) => void;
};

export const ExpandableLoadingStep = ({
	title,
	onClick,
	testId = 'rovo-expandable-step',
	isExpanded = false,
	isLastItem = false,
	status,
	children,
	onExpand,
}: ExpandableLoadingStepProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<LoadingStepContainer testId={testId} status={status} isLastItem={isLastItem}>
			<Pressable
				xcss={cx(styles.titleContainer, styles.expandablePressable)}
				testId={testId}
				onClick={() => {
					onClick();
					onExpand?.(!isExpanded);
				}}
				data-expanded={isExpanded}
			>
				<Box>
					<LoadingStepTitle title={title} />
				</Box>
				<Box xcss={styles.chevronContainer}>
					{isExpanded ? (
						<ChevronDownIcon
							label={formatMessage(intlMessages.collapseIconLabel)}
							size="small"
							color={token('color.icon.subtle')}
						/>
					) : (
						<ChevronRightIcon
							label={formatMessage(intlMessages.expandIconLabel)}
							size="small"
							color={token('color.icon.subtle')}
						/>
					)}
				</Box>
			</Pressable>
			<ExitingPersistence>
				{isExpanded && (
					<FadeIn entranceDirection="top" exitDirection="top" duration="medium">
						{(motionProps) => {
							return (
								<div {...motionProps}>
									<Box xcss={styles.expandedContent}>{children}</Box>
								</div>
							);
						}}
					</FadeIn>
				)}
			</ExitingPersistence>
		</LoadingStepContainer>
	);
};
